import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';

const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const createMarkdownInstance = (linkify = true) => {
  const md = MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });

  // Renderers customizados para remover formatação de lista mas manter o conteúdo
  // Remove tags <ul>, <ol> mas mantém o conteúdo dos <li> como texto simples
  md.renderer.rules.bullet_list_open = () => '';
  md.renderer.rules.bullet_list_close = () => '';
  md.renderer.rules.ordered_list_open = () => '';
  md.renderer.rules.ordered_list_close = () => '';
  
  // CRÍTICO: list_item_open precisa retornar vazio mas o conteúdo interno será renderizado
  // list_item_close renderiza o conteúdo dos children antes de fechar
  md.renderer.rules.list_item_open = () => '';
  md.renderer.rules.list_item_close = (tokens, idx, options, env, self) => {
    // Renderiza o conteúdo interno do item antes de fechar
    // Isso garante que o texto "32. 1" seja preservado
    const token = tokens[idx];
    let result = '';
    
    // Renderiza todos os children do token
    if (token.children) {
      for (let i = 0; i < token.children.length; i++) {
        const child = token.children[i];
        result += self.renderToken(token.children, i, options);
      }
    }
    
    return result + '\n';
  };

  return md;
};

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

class MessageFormatter {
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.md = createMarkdownInstance(linkify);
  }

  formatMessage() {
    let updatedMessage = this.message;
    
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    
    // Substituir sintaxe de lista ANTES do markdown processar
    // Usa espaço não-quebrável (\u00A0) em vez de espaço normal após o número
    // Isso impede que o markdown-it reconheça como lista, mas mantém visual idêntico
    const replacements = new Map();
    let replaceIndex = 0;
    
    // Substitui "32. texto" por "32\u00A0. texto" (espaço não-quebrável)
    updatedMessage = updatedMessage.replace(/^(\d+)\.(\s+)/gm, (match, num, space) => {
      const placeholder = `__LIST_NUM_${replaceIndex}__`;
      replacements.set(placeholder, match);
      replaceIndex++;
      // Espaço não-quebrável não é reconhecido como lista pelo markdown-it
      return `${num}\u00A0.${space}`;
    });
    
    // Substitui "- texto" por "– texto" (en dash, não reconhecido como lista)
    updatedMessage = updatedMessage.replace(/^-\s+/gm, match => {
      const placeholder = `__LIST_BULLET_${replaceIndex}__`;
      replacements.set(placeholder, match);
      replaceIndex++;
      return `–${match.slice(1)}`; // En dash (U+2013) em vez de hífen
    });
    
    let rendered = this.md.render(updatedMessage);
    
    // Restaura os padrões originais após renderização
    replacements.forEach((original, placeholder) => {
      // Escapa caracteres especiais do placeholder para regex
      const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Substitui o placeholder pelo texto original
      rendered = rendered.replace(new RegExp(escapedPlaceholder, 'g'), original);
    });
    
    // Também restaura espaços não-quebráveis e en dashes diretamente
    rendered = rendered.replace(/(\d+)\u00A0\.(\s+)/g, '$1.$2'); // Restaura "32. "
    rendered = rendered.replace(/–(\s+)/g, '-$1'); // Restaura "- "
    
    // Pós-processamento: Remove tags de lista mas preserva o conteúdo completo
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rendered, 'text/html');
      
      // Encontra todas as listas
      const lists = doc.querySelectorAll('ul, ol');
      lists.forEach(list => {
        // Para cada item da lista, converte em parágrafo preservando o conteúdo
        const items = list.querySelectorAll('li');
        items.forEach(item => {
          const p = doc.createElement('p');
          p.innerHTML = item.innerHTML;
          list.parentNode.insertBefore(p, list);
        });
        list.remove();
      });
      
      rendered = doc.body.innerHTML;
    } catch (e) {
      // Fallback: regex se DOMParser falhar
      rendered = rendered
        .replace(/<\/?[uo]l[^>]*>/gi, '')
        .replace(/<li[^>]*>/gi, '<p>')
        .replace(/<\/li>/gi, '</p>');
    }
    
    return rendered;
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
